import { useMemo } from 'react';

import { Alert, ShopToGiveCauseAllocation, Stack } from 'ui';
import { UseFormReturnType, useForm } from 'ui/Form';

import { useFormListValidator } from '../../hooks';

type LocalCauseSelections = { cause_id: string; split: number; cause: { name: string } }[];

type AllocationForm = {
  allocations: {
    [causeId: string]: number | string;
  };
  remainder: number;
};

type UseAllocationForm = UseFormReturnType<AllocationForm>;

const ShopToGiveAllocations = ({ causeSelections }: { causeSelections: LocalCauseSelections }) => {
  const initialValues = useMemo(() => {
    const _initialValues = {};
    causeSelections?.map((selection) => (_initialValues[selection.cause_id] = selection.split));
    return { allocations: _initialValues, remainder: 0 };
  }, [causeSelections]);

  const form = useForm<AllocationForm>({
    initialValues: {
      allocations: initialValues.allocations,
      remainder: initialValues.remainder,
    },
    validate: {
      allocations: (value) => {
        const splits = Object.keys(value).map((causeId) => Number(value[causeId]));
        const remainder = splits.reduce((sum, split) => sum + split, 0);
        return remainder > 100 ? true : null;
      },
    },
    transformValues: (values) => {
      const { allocations } = values;
      const splits = Object.keys(allocations).map((causeId) => Number(allocations[causeId]));
      const remainder = 100 - splits.reduce((sum, split) => sum + split, 0);
      return {
        allocations: allocations,
        remainder: remainder,
      };
    },
  });

  const isValid = useMemo(() => form.isValid(), [form]);

  useFormListValidator({ form, id: 'shopToGiveAllocationSplits' });

  return (
    <Stack gap="sm">
      <Alert.Collapse in={isValid == false} type={'error'}>
        Cash back allocated to your causes must be 100% or less
      </Alert.Collapse>
      {Object.entries(form.values.allocations)?.map(([key, allocation]) => (
        <ShopToGiveCauseAllocation
          key={key}
          name={causeSelections.find((cause) => cause.cause_id === key)?.cause.name ?? ''}
          value={allocation ?? 0}
          onChange={(value) => form.setFieldValue(`allocations.${key}`, value)}
        />
      ))}

      <ShopToGiveCauseAllocation
        name={'Givebacks Wallet'}
        value={form.getTransformedValues().remainder}
        donateText="Keep"
        icon={false}
        withBorder={false}
      />
    </Stack>
  );
};

type ShopToGiveAllocationForms = {
  shopToGiveAllocationSplits: {
    [causeId: string]: string;
  };
  shopToGiveAllocationRemainder: { remainder: number };
};

export { ShopToGiveAllocations };
export type { ShopToGiveAllocationForms, UseAllocationForm };
